<script setup lang="ts">
import { computed } from 'vue';
import useDialog from '@/composables/useDialog';
import CrossIcon from '@/components/Atoms/Icons/CrossIcon.vue';
import TextModal from '@/components/Atoms/Modals/TextModal.vue';
import SpeakerSolidIcon from '@/components/Atoms/Icons/SpeakerSolidIcon.vue';
import useListenAudio from '@/composables/useListenAudio';
import { useSound } from '@vueuse/sound';
import ButtonRoundedRed from '@/components/Atoms/Buttons/ButtonRoundedRed.vue';
import ButtonRoundedGray from '@/components/Atoms/Buttons/ButtonRoundedGray.vue';
import type { ReturnedValue } from '@vueuse/sound/dist/esm/src/types';

interface Props {
    dialogId: number;
    outSideClosable?: boolean;
    modalClosable?: boolean;
    audio?: string;
    cantBeInterrupt?: boolean;
    icon?: boolean;
    btnLeftText?: string;
    btnLeftFunction?: () => void;
    btnRightText?: string;
    btnRightFunction?: () => void;
}
const props = withDefaults(defineProps<Props>(), {
    modalClosable: true,
    outSideClosable: true,
    cantBeInterrupt: false,
    icon: false,
    audio: '',
    btnLeftText: '',
    btnLeftFunction: undefined,
    btnRightText: '',
    btnRightFunction: undefined,
});

const emits = defineEmits(['closed']);
const { closeAllModals, dialogs } = useDialog();

const handleCloseModal = () => {
    closeAllModals();
    emits('closed');
};

const { listen } = useListenAudio();

const isOpen = computed<boolean>(() => {
    return dialogs[props.dialogId].value;
});

function close() {
    if (props.modalClosable && props.outSideClosable) {
        handleCloseModal();
    }
}
let sound: ReturnedValue | null = null;
if (props.audio !== '') {
    sound = useSound(props.audio, { interrupt: true });
}

function btnLeftWhenClick() {
    if (props.btnLeftFunction === undefined) {
        handleCloseModal();
    } else {
        props.btnLeftFunction();
    }
}

function btnRightWhenClick() {
    if (props.btnRightFunction === undefined) {
        handleCloseModal();
    } else {
        props.btnRightFunction();
    }
}
</script>

<template>
    <div v-if="isOpen">
        <div class="fixed inset-0 z-[1000] bg-gray-500 bg-opacity-75 transition-opacity" @click="close"></div>
        <div class="fixed inset-0 z-[1001] overflow-y-auto" @click="close">
            <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <div
                    class="border-4 border-gray-500 relative transform overflow-hidden rounded-3xl bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl xl:max-w-2xl xl:w-fit"
                    @click.stop
                >
                    <div class="flex items-start">
                        <div class="text-left p-8 w-full">
                            <div v-if="props.icon" class="flex justify-between mb-4">
                                <div class="w-20">
                                    <slot name="icon" />
                                </div>
                                <div class="w-6 h-6">
                                    <button
                                        v-if="props.modalClosable"
                                        type="button"
                                        class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white"
                                        @click="handleCloseModal"
                                    >
                                        <CrossIcon />
                                        <span class="sr-only">Close modal</span>
                                    </button>
                                </div>
                            </div>
                            <div class="grid grid-flow-col grid-cols-[auto_1fr_auto]">
                                <div class="w-12 h-12">
                                    <SpeakerSolidIcon
                                        v-if="props.audio && sound"
                                        class="!w-12 !h-12 p-1.5 ml-auto text-gray-600"
                                        @click="listen(sound, props.cantBeInterrupt)"
                                    />
                                </div>
                                <div class="w-full">
                                    <TextModal class="mr-1 text-gray-600 whitespace-pre-line">
                                        <slot name="texte"></slot>
                                    </TextModal>
                                </div>
                                <div class="w-6 h-6">
                                    <button
                                        v-if="props.modalClosable && !props.icon"
                                        type="button"
                                        class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white"
                                        @click="handleCloseModal"
                                    >
                                        <CrossIcon />
                                        <span class="sr-only">Close modal</span>
                                    </button>
                                </div>
                            </div>
                            <slot name="specific" />
                            <div v-if="btnLeftText && btnRightText" class="flex mt-8 justify-between">
                                <!-- btn left and right -->
                                <ButtonRoundedGray
                                    v-if="btnLeftText"
                                    class="w-5/12 flex justify-start"
                                    @click="btnLeftWhenClick"
                                    >{{ btnLeftText }}</ButtonRoundedGray
                                >
                                <ButtonRoundedRed
                                    v-if="btnRightText"
                                    class="w-5/12 flex justify-end"
                                    @click="btnRightWhenClick"
                                    >{{ btnRightText }}</ButtonRoundedRed
                                >
                            </div>
                            <div v-if="!btnLeftText && btnRightText" class="flex mt-8 justify-end">
                                <ButtonRoundedRed
                                    v-if="btnRightText"
                                    class="flex justify-end"
                                    @click="btnRightWhenClick"
                                    >{{ btnRightText }}</ButtonRoundedRed
                                >
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
